import React from 'react';
import { Shield, Target, Users, Award, TrendingUp, Star } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { aboutContent } from '../data/mock';

const AboutSection = () => {
  const { t } = useTranslation();
  const valueIcons = {
    'Strategic Excellence': Target,
    'Team Coordination': Users,
    'Continuous Improvement': TrendingUp,
    'Sportsmanship': Award,
    'Community Building': Star
  };

  return (
    <section id="about" className="section-spacing bg-page">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Shield className="text-primary" size={32} />
            <span className="caption text-primary">{t('about.subtitle')}</span>
          </div>
          <h2 className="heading-1 text-primary mb-6">
            {t('about.title')}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Mission & Vision */}
          <div className="space-y-12">
            {/* Mission */}
            <div>
              <h3 className="heading-2 text-primary mb-6">{t('about.mission')}</h3>
              <p className="body-large text-secondary leading-relaxed">
                {t('about.missionText')}
              </p>
            </div>

            {/* Vision */}
            <div>
              <h3 className="heading-2 text-primary mb-6">{t('about.vision')}</h3>
              <p className="body-large text-secondary leading-relaxed">
                {t('about.visionText')}
              </p>
            </div>

            {/* Values */}
            <div>
              <h3 className="heading-2 text-primary mb-6">{t('about.values')}</h3>
              <div className="space-y-4">
                {t('about.valuesList').map((value, index) => {
                  const IconComponent = valueIcons[value.key] || Shield;
                  return (
                    <div key={index} className="flex items-center space-x-4 p-4 bg-card rounded-lg card-hover-effect">
                      <IconComponent className="text-primary" size={24} />
                      <span className="body-medium text-secondary font-semibold">{value.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column - Stats & Image */}
          <div className="space-y-12">
            {/* Club Stats */}
            <div className="bg-gray-800 bg-card rounded-lg p-8">
              <h3 className="heading-2 text-primary mb-8 text-center">{t('about.byTheNumbers')}</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 border border-gray-700 rounded-lg">
                  <div className="heading-2 text-primary mb-2">{t('clubInfo.achievements')}</div>
                  <div className="caption text-secondary">{t('clubInfo.achievements_desc')}</div>
                </div>
                <div className="text-center p-4 border border-gray-700 rounded-lg">
                  <div className="heading-2 text-primary mb-2">{t('clubInfo.members')}</div>
                  <div className="caption text-secondary">{t('clubInfo.members_desc')}</div>
                </div>
                <div className="text-center p-4 border border-gray-700 rounded-lg">
                  <div className="heading-2 text-primary mb-2">{t('clubInfo.winrate')}</div>
                  <div className="caption text-secondary">{t('clubInfo.winrate_desc')}</div>
                </div>
                <div className="text-center p-4 border border-gray-700 rounded-lg">
                  <div className="heading-2 text-primary mb-2">{t('team.stats.battlefield')}</div>
                  <div className="caption text-secondary">{t('team.stats.battlefield_desc')}</div>
                </div>
              </div>
            </div>

            {/* Gaming Setup Image */}
            <div className="relative rounded-lg overflow-hidden">
              <img 
                src={`${process.env.PUBLIC_URL}/images/assets/war-team.png`} 
                alt="Battlefield Gameplay"
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-30"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h4 className="heading-3 text-primary mb-2">{t('about.nextGenWarfare')}</h4>
                <p className="body-medium text-secondary">
                  {t('about.nextGenDesc')}
                </p>
              </div>
            </div>

            {/* Community Highlights */}
            <div className="bg-secondary-olive rounded-lg p-8">
              <h3 className="heading-3 text-primary mb-6">{t('about.communityImpact')}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-600">
                  <span className="body-medium text-secondary">{t('about.community.trainingSessions')}</span>
                  <span className="body-medium text-primary font-semibold">200+</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-600">
                  <span className="body-medium text-secondary">{t('about.community.playersCoached')}</span>
                  <span className="body-medium text-primary font-semibold">50+</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="body-medium text-secondary">{t('about.community.discordMembers')}</span>
                  <span className="body-medium text-primary font-semibold">300+</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="mt-20">
          <h3 className="heading-2 text-primary text-center mb-12">{t('about.ourJourney')}</h3>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary opacity-30"></div>
            
            {/* Timeline Items */}
            <div className="space-y-16">
                    {[
                    { 
                      year: '2012 Battlefield 3', 
                      title: 'Foundation', 
                      descriptions: [
                        '● Prototype eSports established with 5 founding members.',
                        '● 3th/4th @ ESL Conquest 5on5 Infantry Liga Otoño 2k12 SIL'
                      ]
                    },
                    {
                      year: '2014 Battlefield 4',
                      title: '[BNL] Battlefield Nordic League ',
                      descriptions: [
                        '● Season 1 [Division 2] Participated in the inaugural season', 
                        '● Season 2 [Division 2] 8th Place'
                      ]
                    },
                                        {
                      year: '2014 Battlefield 4',
                      title: 'ATF CUP 4',
                      descriptions: [,
                        '● 5th/6th Place'
                      ]
                    },
                    {
                      year: '2015 Battlefield 4',
                      title: '[BNL] Battlefield Nordic League ',
                      descriptions: [
                        '● Season 3 [Division 3] 5th Place', 
                        '● Season 4 [Division 5] 3th Place',
                        '● Season 5 [Division 5] 2th Place', 
                        '● Season 6 [Division 4] 3th Place',
                        '● Season 7 [Main Div 2] 2th Place'
                      ]
                    },
                    {
                        year: '2017 Battlefield 1',
                        title: '8v8 Test Cup ',
                        descriptions: [
                          '● [League & Playoffs] 4th Place', 
                        ]
                    },
                    {
                        year: '2018 Battlefield V',
                        title: '5v5 Leagues ',
                        descriptions: [
                          '● [Spanish League]',
                          '● [Lycan League]',
                          '● [BCL International League]',
                        ]
                    },

                    ].map((item, index) => (
                    <div key={index} className={`flex items-center ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                      <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'pl-8'}`}>
                      <div className="bg-card p-6 rounded-lg card-hover-effect">
                        <div className="heading-3 text-primary mb-2">{item.year}</div>
                        <h4 className="body-medium text-primary font-semibold mb-2">{item.title}</h4>
                        {item.descriptions.map((desc, descIndex) => (
                          <p key={descIndex} className="body-medium text-secondary mb-1">{desc}</p>
                        ))}
                      </div>
                      </div>
                      
                      {/* Timeline Dot */}
                  <div className="w-4 h-4 bg-primary rounded-full border-4 border-page relative z-10"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
    );
};


export default AboutSection;